import { Dispatch, SetStateAction } from 'react';
import { ace_index } from '@axo/shared/data-access/types';
import { MultiFilterSelect } from '../MultiFilterSelect';
import { setPageNumber } from '../SearchResults';

interface Props {
  query: ace_index.FindInput;
  setQuery: Dispatch<SetStateAction<ace_index.FindInput>>;
  editable?: boolean;
}

export function Partner({ query, setQuery, editable = false }: Props) {
  const state = getPartnerState(query);
  const values = options.filter((option) => state.includes(option.label));

  return (
    <MultiFilterSelect
      items={options}
      onSelect={(option) => setQuery(addPartner(option))}
      onRemove={(_, i) => setQuery(removePartner(values[i]))}
      stringify={(option) => option.label}
      values={values}
      label="Partner"
      description="Filter applications by partner"
      disabled={!editable}
    />
  );
}

/** A partner may be identified by more than one value for the same field. */
type CampaignValue = string | string[];

interface PartnerOption {
  label: string;
  value: {
    SummaryCampaignContent?: CampaignValue;
    SummaryCampaignName?: CampaignValue;
    SummaryCampaignMedium?: CampaignValue;
  };
}

function toValues(value?: CampaignValue): string[] {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
}

const OTHERS_LABEL = 'Others';

const partnerOptions: PartnerOption[] = [
  {
    label: 'FK Bolig',
    value: {
      SummaryCampaignContent: '1797',
    },
  },
  {
    label: 'FK Bolig 1P',
    value: {
      SummaryCampaignContent: '1797',
      SummaryCampaignMedium: '1952',
    },
  },
  {
    label: 'UOB',
    value: {
      SummaryCampaignContent: '1695',
      SummaryCampaignName: '927',
    },
  },
  {
    label: 'UOB Forbrug',
    value: {
      SummaryCampaignContent: '1695',
      SummaryCampaignName: '926',
    },
  },
  {
    label: 'UOB Forbrug 1P',
    value: {
      SummaryCampaignContent: '1695',
      SummaryCampaignMedium: '1928',
    },
  },
  {
    label: 'UOB 1P',
    value: {
      SummaryCampaignContent: '1695',
      SummaryCampaignMedium: '1927',
    },
  },
  {
    label: 'HKF',
    value: {
      SummaryCampaignContent: '1800',
    },
  },
  {
    label: 'HKF 1P',
    value: {
      SummaryCampaignContent: '1800',
      SummaryCampaignMedium: '1982',
    },
  },
  {
    label: 'VK Kredit',
    value: {
      SummaryCampaignContent: '1716',
    },
  },
  {
    label: 'Draivi Media',
    value: {
      SummaryCampaignContent: ['1370', 'Draivi Media Oy'],
    },
  },
];

const options: { label: string }[] = [
  ...partnerOptions.map(({ label }) => ({ label })),
  { label: OTHERS_LABEL },
];

/** Collect all unique known values per field (used for "Others" NotIn). */
function getAllKnownValues() {
  const content = new Set<string>();
  const name = new Set<string>();
  const medium = new Set<string>();

  for (const { value } of partnerOptions) {
    toValues(value.SummaryCampaignContent).forEach((v) => content.add(v));
    toValues(value.SummaryCampaignName).forEach((v) => name.add(v));
    toValues(value.SummaryCampaignMedium).forEach((v) => medium.add(v));
  }

  return {
    content: Array.from(content),
    name: Array.from(name),
    medium: Array.from(medium),
  };
}

/** Collect unique values from a list of selected partners for the "In" fields. */
function collectInValues(selected: PartnerOption[]) {
  const content = new Set<string>();
  const name = new Set<string>();
  const medium = new Set<string>();

  for (const { value } of selected) {
    toValues(value.SummaryCampaignContent).forEach((v) => content.add(v));
    toValues(value.SummaryCampaignName).forEach((v) => name.add(v));
    toValues(value.SummaryCampaignMedium).forEach((v) => medium.add(v));
  }

  return {
    content: content.size > 0 ? Array.from(content) : undefined,
    name: name.size > 0 ? Array.from(name) : undefined,
    medium: medium.size > 0 ? Array.from(medium) : undefined,
  };
}

/** Clear deprecated single-value fields alongside new In/NotIn fields. */
const CLEARED_DEPRECATED = {
  SummaryCampaignName: undefined,
  SummaryCampaignMedium: undefined,
  SummaryCampaignContent: undefined,
} as const;

/** Build the query fields from a set of selected partner labels. */
function buildQueryFields(selectedLabels: Set<string>): PartnerQueryFields {
  const isOthers = selectedLabels.has(OTHERS_LABEL);

  if (isOthers) {
    const all = getAllKnownValues();
    return {
      ...CLEARED_DEPRECATED,
      SummaryCampaignContentIn: undefined,
      SummaryCampaignContentNotIn:
        all.content.length > 0 ? all.content : undefined,
      SummaryCampaignNameIn: undefined,
      SummaryCampaignNameNotIn: all.name.length > 0 ? all.name : undefined,
      SummaryCampaignMediumIn: undefined,
      SummaryCampaignMediumNotIn:
        all.medium.length > 0 ? all.medium : undefined,
    };
  }

  const selected = partnerOptions.filter((p) => selectedLabels.has(p.label));

  if (selected.length === 0) {
    return {
      ...CLEARED_DEPRECATED,
      SummaryCampaignContentIn: undefined,
      SummaryCampaignContentNotIn: undefined,
      SummaryCampaignNameIn: undefined,
      SummaryCampaignNameNotIn: undefined,
      SummaryCampaignMediumIn: undefined,
      SummaryCampaignMediumNotIn: undefined,
    };
  }

  const values = collectInValues(selected);
  return {
    ...CLEARED_DEPRECATED,
    SummaryCampaignContentIn: values.content,
    SummaryCampaignContentNotIn: undefined,
    SummaryCampaignNameIn: values.name,
    SummaryCampaignNameNotIn: undefined,
    SummaryCampaignMediumIn: values.medium,
    SummaryCampaignMediumNotIn: undefined,
  };
}

function addPartner(option: {
  label: string;
}): SetStateAction<ace_index.FindInput> {
  return (query) => {
    const current = getSelectedLabels(query);

    if (option.label === OTHERS_LABEL) {
      // Others is mutually exclusive: toggle it, clear everything else
      if (current.has(OTHERS_LABEL)) {
        current.delete(OTHERS_LABEL);
      } else {
        current.clear();
        current.add(OTHERS_LABEL);
      }
    } else {
      // Specific partner: toggle it, remove Others if present
      current.delete(OTHERS_LABEL);
      if (current.has(option.label)) {
        current.delete(option.label);
      } else {
        current.add(option.label);
      }
    }

    return {
      ...setPageNumber(1)(query),
      ...buildQueryFields(current),
    };
  };
}

function removePartner(option: {
  label: string;
}): SetStateAction<ace_index.FindInput> {
  return (query) => {
    const current = getSelectedLabels(query);
    current.delete(option.label);

    return {
      ...setPageNumber(1)(query),
      ...buildQueryFields(current),
    };
  };
}

/**
 * Derive which partner labels are currently selected by inspecting the query.
 * Returns labels as an array of strings for filtering the options list.
 */
function getPartnerState(query: ace_index.FindInput): string[] {
  // "Others" mode: any NotIn field is set
  if (
    query.SummaryCampaignContentNotIn?.length ||
    query.SummaryCampaignNameNotIn?.length ||
    query.SummaryCampaignMediumNotIn?.length
  ) {
    return [OTHERS_LABEL];
  }

  // "In" mode: match partners whose values are all present in the In fields
  const contentIn = new Set(query.SummaryCampaignContentIn ?? []);
  const nameIn = new Set(query.SummaryCampaignNameIn ?? []);
  const mediumIn = new Set(query.SummaryCampaignMediumIn ?? []);

  return partnerOptions
    .filter(({ value }) => {
      const content = toValues(value.SummaryCampaignContent);
      const name = toValues(value.SummaryCampaignName);
      const medium = toValues(value.SummaryCampaignMedium);

      // At least one field must be defined to match
      if (content.length + name.length + medium.length === 0) return false;

      return (
        content.every((v) => contentIn.has(v)) &&
        name.every((v) => nameIn.has(v)) &&
        medium.every((v) => mediumIn.has(v))
      );
    })
    .map(({ label }) => label);
}

/**
 * Get the currently selected partner labels as a mutable Set,
 * for use in add/remove logic.
 */
function getSelectedLabels(query: ace_index.FindInput): Set<string> {
  return new Set(getPartnerState(query));
}

type PartnerQueryFields = Pick<
  ace_index.FindInput,
  | 'SummaryCampaignContentIn'
  | 'SummaryCampaignContentNotIn'
  | 'SummaryCampaignNameIn'
  | 'SummaryCampaignNameNotIn'
  | 'SummaryCampaignMediumIn'
  | 'SummaryCampaignMediumNotIn'
>;
